import type {
  GrantedPerkStatus,
  MyPerk,
  PerkGrantPolicyKind,
} from '../domain/entities/platform.entity';

/**
 * Rótulos pt-BR desta tela. São `Record` pequenos sobre enums **estáveis** do
 * back (5 status, 5 kinds) — não sobre dado que vive no banco. O
 * admin-dashboard tem os seus, com o vocabulário de operador; promover para um
 * package compartilhado exigiria o primeiro package de domínio do monorepo, e o
 * componente do admin mostra coisas que esta tela não deve mostrar.
 *
 * O nome da plataforma **não** está aqui: vem no `platformName` de
 * `GET /auth/me/platforms`. Um mapa local por slug duplicaria a fonte e
 * silenciaria plataforma nova (aparecia o slug técnico na tela).
 */

/** Nome comercial da plataforma; cai no identificador técnico se aparecer um novo. */
const PLATFORM_LABELS: Record<string, string> = {
  'aristo-medq': 'Aristo/MedQ',
  'admin-dashboard': 'Painel administrativo',
  'user-dashboard': 'Minha conta',
  'pipefy-admin': 'Pipefy Admin',
};

export function platformLabel(platform: string): string {
  return PLATFORM_LABELS[platform] ?? platform;
}

export const STATUS_LABELS: Record<
  GrantedPerkStatus,
  { label: string; tone: 'success' | 'warning' | 'error' | 'default' }
> = {
  active: { label: 'Ativo', tone: 'success' },
  expired: { label: 'Expirado', tone: 'error' },
  revoked: { label: 'Revogado', tone: 'error' },
  depleted: { label: 'Esgotado', tone: 'warning' },
  suspended: { label: 'Suspenso', tone: 'warning' },
};

export function statusLabel(status: GrantedPerkStatus) {
  return STATUS_LABELS[status] ?? { label: status, tone: 'default' as const };
}

/** O que a concessão entrega, na voz de quem recebeu. */
export const GRANT_POLICY_LABELS: Record<PerkGrantPolicyKind, string> = {
  time_limited_credit: 'Crédito com prazo',
  usage_limited_credit: 'Crédito por uso limitado',
  time_bounded_recurring_credit: 'Crédito que renova',
  time_bounded_unlimited_usage: 'Uso ilimitado com prazo',
  permanent: 'Permanente',
};

export function grantPolicyLabel(kind: PerkGrantPolicyKind): string {
  return GRANT_POLICY_LABELS[kind] ?? kind;
}

const MS_PER_DAY = 86_400_000;

export function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** Duração aproximada em linguagem natural ("3 dias", "5 horas"). */
function formatDuration(ms: number): string {
  const days = Math.round(ms / MS_PER_DAY);
  if (days >= 1) return `${days} ${days === 1 ? 'dia' : 'dias'}`;
  const hours = Math.round(ms / 3_600_000);
  if (hours >= 1) return `${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  const minutes = Math.max(1, Math.round(ms / 60_000));
  return `${minutes} min`;
}

/** "renova a cada 7 dias". `null` quando o grant não renova. */
export function formatRenewInterval(ms: number | null): string | null {
  if (!ms || ms <= 0) return null;
  return `renova a cada ${formatDuration(ms)}`;
}

/**
 * Quanto falta para o prazo, para o que ainda vale. `null` quando já passou —
 * aí quem conta a história é o status, não uma contagem negativa.
 */
export function formatTimeLeft(iso: string | null): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const remaining = target - Date.now();
  if (remaining <= 0) return null;
  return `faltam ${formatDuration(remaining)}`;
}

/**
 * Cota de uso. `null` em grant de janela — ali não existe contador, e escrever
 * "ilimitado" afirmaria mais do que o dado diz.
 */
export function formatQuota(perk: MyPerk): string | null {
  const { maxUsages, remainingUsages } = perk;

  if (maxUsages !== null) {
    const remaining = remainingUsages ?? maxUsages;
    const used = Math.max(0, maxUsages - remaining);
    return `${used} de ${maxUsages} usados · ${remaining} ${
      remaining === 1 ? 'restante' : 'restantes'
    }`;
  }

  if (remainingUsages !== null) {
    return `${remainingUsages} ${
      remainingUsages === 1 ? 'uso restante' : 'usos restantes'
    }`;
  }

  return null;
}
